import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";

import Title from "../../atoms/Title/Title";
import Text from "../../atoms/Text/Text";
import StyledButton from "../../atoms/StyledButton/StyledButton";

import { SubscriptionItem, OptionItem } from "../../../types/rtk/currentSubscriptionsOptions/currentSubscriptionsOptions";
import ManageSubscriptionsOptionsBlock from "../../molecules/ManageSubscriptionsOptionsBlock/ManageSubscriptionsOptionsBlock";
import { useNavigateWithParams } from "../../../hooks/useNavigateWithParams";
import { AppRoutes } from "../../../routes/AppRoutes";

interface Props {
  data: {
    tracker_name?: string;
    tracker_subscriptions: SubscriptionItem[];
    subscription_options: OptionItem[];
    account_options: OptionItem[];
  };
  onActivate: (item: SubscriptionItem | OptionItem) => void;
  onEdit: (item: SubscriptionItem | OptionItem) => void;
  isDisabled?: boolean;
}

const ManageSubscriptionsOptionsList = ({ data, onActivate, onEdit, isDisabled }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigateWithParams();

  return (
    <Box className="ManageSubscriptionsOptionsList">
      {/* Title */}
      <Box className="ManageSubscriptionsOptionsListSubtitleContainer">
        {data?.tracker_name && (
          <Title variant="h1">
            {(() => {
              const raw = t("subscription:MANAGE_PAGE.TRACKER_SUBSCRIPTIONS_OPTIONS.TITLE", { name: "___NAME___" });
              const [before, after] = raw.split("___NAME___");
              return (
                <>
                  {before}
                  <Box component="span" className="tracker-name-highlight">{data.tracker_name}</Box>
                  {after}
                </>
              );
            })()}
          </Title>
        )}
      </Box>

      {/* Subscriptions list */}
      {data.tracker_subscriptions.length > 0 ? (
        <Box className="ManageSubscriptionsOptionsList">
          {data.tracker_subscriptions.map((item, idx) => (
            <ManageSubscriptionsOptionsBlock
              key={`subscription-${idx}`}
              item={item}
              onActivate={() => onActivate(item)}
              onEdit={() => onEdit(item)}
              isDisabled={isDisabled}
            />
          ))}
        </Box>
      ) : (
        <Box className="ManageSubscriptionsOptionsListNoDefault NoSubscriptionButton">
          <Text children={t("subscription:MANAGE_PAGE.NO_SUBSCRIPTION")} />
          <StyledButton
            className="left small"
            children={t("subscription:MANAGE_PAGE.ADD_SUBSCRIPTION_BUTTON")}
            onClick={() => navigate(AppRoutes.SUBSCRIPTIONS)}
          />
        </Box>
      )}

      {/* Tracker options list */}
      {data.subscription_options.length > 0 ? (
        <Box className="ManageSubscriptionsOptionsList">
          {data.subscription_options.map((item, idx) => (
            <ManageSubscriptionsOptionsBlock
              key={`option-${idx}`}
              item={item}
              onActivate={() => onActivate(item)}
              onEdit={() => onEdit(item)}
              isDisabled={isDisabled}
            />
          ))}
        </Box>
      ) : null
      }

      {/* Account options title */}
      <Box className="ManageSubscriptionsOptionsListSubtitleContainer AccountOptionsTitle">
        <Title
          variant="h1"
          children={t("subscription:MANAGE_PAGE.ACCOUNT_OPTIONS.TITLE")}
        />
        <Text children={t("subscription:MANAGE_PAGE.ACCOUNT_OPTIONS.DESCRIPTION")} />
      </Box>

      {/* Account options list */}
      {data.account_options.length > 0 ? (
        <Box className="AccountOptionsList">
          {data.account_options.map((item, idx) => (
            <ManageSubscriptionsOptionsBlock
              key={`account-option-${idx}`}
              item={item}
              onActivate={() => onActivate(item)}
              onEdit={() => onEdit(item)}
              isDisabled={isDisabled}
            />
          ))}
        </Box>
      ) : (
        <Text children="No account options" />
      )}
    </Box>
  );
};

export default ManageSubscriptionsOptionsList;